import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiDownload, FiRefreshCw, FiEye, FiCreditCard, FiDollarSign, FiTrendingUp, FiArrowUp,  FiX,  FiAlertCircle } from 'react-icons/fi';
import api from '../api';

const PaymentManagement = () => {
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    paymentType: 'all',
    tenantId: '',
    startDate: '',
    endDate: '',
    search: ''
  });

  // Selected payment for details
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [processingRefund, setProcessingRefund] = useState(false);

  // Alert
  const [alert, setAlert] = useState({ show: false, type: 'success', message: '' });

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: 'success', message: '' }), 5000);
  };

  // ============================================
  // FETCH PAYMENTS
  // ============================================
  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key] || params[key] === 'all' || params[key] === '') {
          delete params[key];
        }
      });

      const response = await api.get('/api/super-admin/payments', { params });
      
      if (response.data.success) {
        setPayments(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      showAlert('error', 'Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  // ============================================
  // FETCH ANALYTICS
  // ============================================
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await api.get('/api/super-admin/payments/analytics', {
        params: { period: 'month' }
      });
      
      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, []);

  // ============================================
  // FETCH WALLET
  // ============================================
  const fetchWallet = async (tenantId) => {
    try {
      const response = await api.get(`/api/super-admin/tenants/${tenantId}/wallet`);
      if (response.data.success) {
        setWalletData(response.data.data);
        setShowWalletModal(true);
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
      showAlert('error', 'Failed to fetch wallet data');
    }
  };

  // ============================================
  // PROCESS REFUND
  // ============================================
  const handleProcessRefund = async () => {
    if (!selectedPayment) return;
    if (!refundAmount || parseFloat(refundAmount) <= 0) {
      showAlert('error', 'Please enter a valid refund amount');
      return;
    }

    setProcessingRefund(true);
    try {
      const response = await api.post(`/api/super-admin/payments/${selectedPayment._id}/refund`, {
        amount: parseFloat(refundAmount),
        reason: refundReason || 'Admin initiated refund'
      });

      if (response.data.success) {
        showAlert('success', 'Refund processed successfully');
        setShowRefundModal(false);
        setRefundAmount('');
        setRefundReason('');
        fetchPayments();
        fetchAnalytics();
      }
    } catch (error) {
      console.error('Error processing refund:', error);
      showAlert('error', error.response?.data?.message || 'Failed to process refund');
    } finally {
      setProcessingRefund(false);
    }
  };

  // ============================================
  // EXPORT CSV
  // ============================================
  const handleExportCSV = async () => {
    try {
      const params = {
        status: filters.status === 'all' ? undefined : filters.status,
        tenantId: filters.tenantId || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      };

      const response = await api.get('/api/super-admin/payments/export', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payments-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showAlert('success', 'CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      showAlert('error', 'Failed to export CSV');
    }
  };

  // ============================================
  // INITIAL LOAD
  // ============================================
  useEffect(() => {
    fetchPayments();
    fetchAnalytics();
  }, [fetchPayments, fetchAnalytics]);

  // ============================================
  // RENDER ANALYTICS CARDS
  // ============================================
  const renderAnalyticsCards = () => {
    if (!analytics) return null;
    
    const { summary } = analytics;
    
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 font-medium">Total Revenue</p>
              <p className="text-xl font-bold text-slate-900">₹{summary.revenue.total.toFixed(2)}</p>
            </div>
            <div className="p-2 bg-emerald-50 rounded-lg">
              <FiDollarSign className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
          <div className="mt-2 flex items-center gap-1">
            <FiTrendingUp className="w-3 h-3 text-emerald-500" />
            <span className="text-xs text-slate-500">Net: ₹{summary.revenue.netRevenue.toFixed(2)}</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 font-medium">Total Payments</p>
              <p className="text-xl font-bold text-slate-900">{summary.payments.total}</p>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg">
              <FiCreditCard className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <div className="mt-2 flex items-center gap-1">
            <span className="text-xs text-slate-500">{summary.payments.credits} credits sold</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 font-medium">Refunds</p>
              <p className="text-xl font-bold text-slate-900">{summary.refunds.total}</p>
            </div>
            <div className="p-2 bg-rose-50 rounded-lg">
              <FiArrowUp className="w-5 h-5 text-rose-600" />
            </div>
          </div>
          <div className="mt-2 flex items-center gap-1">
            <span className="text-xs text-slate-500">₹{summary.refunds.amount.toFixed(2)} refunded</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 font-medium">Tax Collected</p>
              <p className="text-xl font-bold text-slate-900">₹{summary.revenue.tax.toFixed(2)}</p>
            </div>
            <div className="p-2 bg-amber-50 rounded-lg">
              <FiAlertCircle className="w-5 h-5 text-amber-600" />
            </div>
          </div>
          <div className="mt-2 flex items-center gap-1">
            <span className="text-xs text-slate-500">Inclusive/Exclusive tax</span>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER PAYMENT TABLE
  // ============================================
  const renderPaymentTable = () => {
    const statusColors = {
      pending: 'bg-amber-100 text-amber-700',
      completed: 'bg-emerald-100 text-emerald-700',
      failed: 'bg-rose-100 text-rose-700',
      refunded: 'bg-slate-100 text-slate-700'
    };

    return (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left p-3 text-xs font-bold text-slate-500 uppercase">Payment ID</th>
                <th className="text-left p-3 text-xs font-bold text-slate-500 uppercase">Tenant</th>
                <th className="text-left p-3 text-xs font-bold text-slate-500 uppercase">Type</th>
                <th className="text-left p-3 text-xs font-bold text-slate-500 uppercase">Credits</th>
                <th className="text-left p-3 text-xs font-bold text-slate-500 uppercase">Amount</th>
                <th className="text-left p-3 text-xs font-bold text-slate-500 uppercase">Status</th>
                <th className="text-left p-3 text-xs font-bold text-slate-500 uppercase">Date</th>
                <th className="text-center p-3 text-xs font-bold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-slate-400">
                    No payments found
                  </td>
                </tr>
              ) : (
                payments.map((payment) => (
                  <tr key={payment._id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 text-xs font-mono text-slate-600">
                      {payment._id.slice(-8)}
                    </td>
                    <td className="p-3 text-xs">
                      <div className="font-medium text-slate-800">{payment.tenant?.name || payment.tenantId}</div>
                      <div className="text-[10px] text-slate-400">{payment.tenantId}</div>
                    </td>
                    <td className="p-3 text-xs capitalize">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-[10px] font-medium">
                        {payment.paymentType}
                      </span>
                      {payment.communicationType && (
                        <span className="ml-1 px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full text-[10px] font-medium">
                          {payment.communicationType}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-xs font-medium">
                      {payment.credits || 0}
                    </td>
                    <td className="p-3 text-xs font-bold text-slate-900">
                      ₹{payment.amount?.toFixed(2)}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColors[payment.status] || 'bg-slate-100 text-slate-700'}`}>
                        {payment.status}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-slate-500">
                      {payment.createdAt ? new Date(payment.createdAt).toLocaleDateString('en-IN') : 'N/A'}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowPaymentModal(true);
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                        {payment.status === 'completed' && (
                          <button
                            onClick={() => {
                              setSelectedPayment(payment);
                              setRefundAmount(payment.amount?.toFixed(2) || '');
                              setRefundReason('');
                              setShowRefundModal(true);
                            }}
                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Refund"
                          >
                            <FiArrowUp className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => fetchWallet(payment.tenantId)}
                          className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="View Wallet"
                        >
                          <FiDollarSign className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER PAGINATION
  // ============================================
  const renderPagination = () => {
    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-slate-200 rounded-b-xl">
        <div className="text-xs text-slate-500">
          Showing {payments.length} of {pagination.total} payments
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page === 1}
            className="px-3 py-1 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-3 py-1 text-sm font-medium">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page === pagination.totalPages}
            className="px-3 py-1 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Payment Management</h1>
          <p className="text-sm text-slate-500">Manage all payments, refunds, and tenant wallets</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            <FiDownload className="w-4 h-4" /> Export
          </button>
          <button
            onClick={() => {
              fetchPayments();
              fetchAnalytics();
            }}
            className="flex items-center gap-2 px-3 py-2 bg-indigo-600 rounded-lg text-sm font-bold text-white hover:bg-indigo-700"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Alert */}
      {alert.show && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          alert.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
          alert.type === 'error' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
          'bg-amber-50 text-amber-700 border border-amber-200'
        }`}>
          {alert.message}
        </div>
      )}

      {/* Analytics Cards */}
      {renderAnalyticsCards()}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-bold text-slate-700 mb-1">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by ID or Order ID..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="min-w-[120px]">
            <label className="block text-xs font-bold text-slate-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>

          <div className="min-w-[120px]">
            <label className="block text-xs font-bold text-slate-700 mb-1">Type</label>
            <select
              value={filters.paymentType}
              onChange={(e) => setFilters(prev => ({ ...prev, paymentType: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Types</option>
              <option value="credit">Credit</option>
              <option value="bundle">Bundle</option>
            </select>
          </div>

          <div className="min-w-[130px]">
            <label className="block text-xs font-bold text-slate-700 mb-1">Tenant ID</label>
            <input
              type="text"
              placeholder="Tenant ID"
              value={filters.tenantId}
              onChange={(e) => setFilters(prev => ({ ...prev, tenantId: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <button
            onClick={fetchPayments}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700"
          >
            Apply Filters
          </button>
          <button
            onClick={() => {
              setFilters({ status: 'all', paymentType: 'all', tenantId: '', startDate: '', endDate: '', search: '' });
              setPagination(prev => ({ ...prev, page: 1 }));
              setTimeout(fetchPayments, 100);
            }}
            className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm font-bold hover:bg-slate-50"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Payment Table */}
      {renderPaymentTable()}
      {renderPagination()}

      {/* Payment Details Modal */}
      {showPaymentModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-900">Payment Details</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Payment ID</p>
                  <p className="font-medium">{selectedPayment._id}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Status</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    selectedPayment.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                    selectedPayment.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    selectedPayment.status === 'refunded' ? 'bg-slate-100 text-slate-700' :
                    'bg-rose-100 text-rose-700'
                  }`}>
                    {selectedPayment.status}
                  </span>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Amount</p>
                  <p className="font-bold text-lg">₹{selectedPayment.amount?.toFixed(2)}</p>
                  <p className="text-xs text-slate-400">Subtotal: ₹{selectedPayment.subtotal?.toFixed(2)}</p>
                  <p className="text-xs text-slate-400">Tax: ₹{selectedPayment.taxAmount?.toFixed(2)} ({selectedPayment.taxName} @ {selectedPayment.taxPercentage}%)</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Type</p>
                  <p className="font-medium capitalize">{selectedPayment.paymentType}</p>
                  {selectedPayment.communicationType && (
                    <p className="text-xs text-slate-400 capitalize">{selectedPayment.communicationType}</p>
                  )}
                  {selectedPayment.credits > 0 && (
                    <p className="text-xs text-slate-400">{selectedPayment.credits} credits</p>
                  )}
                  {selectedPayment.bundleName && (
                    <p className="text-xs text-slate-400">Bundle: {selectedPayment.bundleName}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500 text-xs">Razorpay</p>
                  <p className="text-xs font-mono">Order: {selectedPayment.razorpayOrderId}</p>
                  <p className="text-xs font-mono">Payment: {selectedPayment.razorpayPaymentId || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500 text-xs">Timestamps</p>
                  <p className="text-xs">Created: {new Date(selectedPayment.createdAt).toLocaleString('en-IN')}</p>
                  {selectedPayment.completedAt && (
                    <p className="text-xs">Completed: {new Date(selectedPayment.completedAt).toLocaleString('en-IN')}</p>
                  )}
                  {selectedPayment.refundedAt && (
                    <p className="text-xs text-amber-600">Refunded: {new Date(selectedPayment.refundedAt).toLocaleString('en-IN')}</p>
                  )}
                </div>
              </div>
              {selectedPayment.status === 'completed' && (
                <div className="pt-4 border-t border-slate-200 flex gap-2">
                  <button
                    onClick={() => {
                      setRefundAmount(selectedPayment.amount?.toFixed(2) || '');
                      setRefundReason('');
                      setShowRefundModal(true);
                      setShowPaymentModal(false);
                    }}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700"
                  >
                    Process Refund
                  </button>
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-bold hover:bg-slate-200"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-900">Process Refund</h3>
              <button
                onClick={() => setShowRefundModal(false)}
                className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Payment Amount</label>
                <p className="text-lg font-bold text-slate-900">₹{selectedPayment.amount?.toFixed(2)}</p>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Refund Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={selectedPayment.amount}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-slate-400 mt-1">Maximum: ₹{selectedPayment.amount?.toFixed(2)}</p>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Reason</label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Reason for refund..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none h-20"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowRefundModal(false)}
                  className="flex-1 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm font-bold hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProcessRefund}
                  disabled={processingRefund}
                  className="flex-1 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 disabled:opacity-50"
                >
                  {processingRefund ? 'Processing...' : 'Process Refund'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Modal */}
      {showWalletModal && walletData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-bold text-slate-900">Tenant Wallet</h3>
              <button
                onClick={() => setShowWalletModal(false)}
                className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-blue-600 font-medium">Email Credits</p>
                  <p className="text-2xl font-bold text-blue-700">{walletData.wallet?.emailCredits || 0}</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-purple-600 font-medium">SMS Credits</p>
                  <p className="text-2xl font-bold text-purple-700">{walletData.wallet?.smsCredits || 0}</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs font-bold text-slate-500 mb-2">Tenant Info</p>
                <p className="text-sm font-medium">{walletData.tenant?.name || 'Unknown'}</p>
                <p className="text-xs text-slate-400">{walletData.tenant?.email || walletData.tenant?.tenantId}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500 mb-2">Recent Transactions</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {walletData.recentTransactions?.slice(0, 10).map((tx, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1 border-b border-slate-100">
                      <span className="text-slate-600">{tx.description}</span>
                      <span className={`font-bold ${tx.amount > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {tx.amount > 0 ? '+' : ''}{tx.amount}
                      </span>
                    </div>
                  ))}
                  {(!walletData.recentTransactions || walletData.recentTransactions.length === 0) && (
                    <p className="text-xs text-slate-400 text-center py-4">No recent transactions</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowWalletModal(false)}
                className="w-full py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;